// Прокручиваемая история диалога + текущие (потоковые) реплики и индикатор
// «агент думает…», пока ждём ответ. Автопрокрутка к низу.

import { useEffect, useRef } from 'react'
import type { AgentIdentity, DialogTurn } from '../types'
import { agentIconUrl } from '../net/mode'
import { RichText } from './RichText'

interface Props {
  history: DialogTurn[]
  liveUserText: string
  liveAgentText: string
  thinking: boolean
  currentAgent: AgentIdentity | null
}

export function DialogHistory({ history, liveUserText, liveAgentText, thinking, currentAgent }: Props) {
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [history.length, liveUserText, liveAgentText, thinking])

  const empty = history.length === 0 && !liveUserText && !liveAgentText && !thinking
  // Индикатор «думает» показываем, только пока ЖДЁМ текст (не во время зачитывания).
  const showThinking = thinking && !liveAgentText

  return (
    <div className="history" role="log" aria-live="polite">
      {empty && (
        <p className="history__empty">
          Нажми кнопку микрофона и говори — или напиши сообщение внизу.
        </p>
      )}

      {history.map((t) => (
        <Bubble key={t.id} turn={t} />
      ))}

      {liveUserText && <Bubble turn={{ speaker: 'user', text: liveUserText }} live />}
      {liveAgentText && (
        <Bubble
          turn={{
            speaker: 'agent',
            text: liveAgentText,
            agentId: currentAgent?.id,
            agentName: currentAgent?.name,
            agentEmoji: currentAgent?.emoji,
          }}
          live
        />
      )}

      {showThinking && <ThinkingBubble agent={currentAgent} />}

      <div ref={endRef} />
    </div>
  )
}

type PartialTurn = Pick<DialogTurn, 'speaker' | 'text' | 'agentId' | 'agentName' | 'agentEmoji'>

function AgentAvatar({ id, emoji }: { id?: string; emoji?: string }) {
  const icon = agentIconUrl(id)
  if (icon) return <img className="msg__avatar" src={icon} alt="" />
  return <span className="msg__avatar msg__avatar--emoji">{emoji || '🤖'}</span>
}

function Bubble({ turn, live }: { turn: PartialTurn; live?: boolean }) {
  if (turn.speaker === 'agent') {
    return (
      <div className="row row--agent">
        <AgentAvatar id={turn.agentId} emoji={turn.agentEmoji} />
        <div className={`bubble bubble--agent ${live ? 'bubble--live' : ''}`}>
          <span className="bubble__who">{turn.agentName || 'Агент'}</span>
          <p className="bubble__text">
            <RichText text={turn.text} />
            {live && <span className="bubble__caret" />}
          </p>
        </div>
      </div>
    )
  }
  return (
    <div className={`bubble bubble--user ${live ? 'bubble--live' : ''}`}>
      <span className="bubble__who">Я</span>
      <p className="bubble__text">
        <RichText text={turn.text} />
        {live && <span className="bubble__caret" />}
      </p>
    </div>
  )
}

function ThinkingBubble({ agent }: { agent: AgentIdentity | null }) {
  return (
    <div className="row row--agent">
      <AgentAvatar id={agent?.id} emoji={agent?.emoji} />
      <div className="bubble bubble--agent bubble--thinking">
        <span className="bubble__who">{agent?.name || 'Агент'}</span>
        <p className="bubble__text bubble__thinking">
          думает
          <span className="dots"><i></i><i></i><i></i></span>
        </p>
      </div>
    </div>
  )
}
